import { cva } from 'class-variance-authority';

import { ANIMATION_CLASSES } from '@/lib/shared-constants';
import {
  textScaleVariants,
  buttonHeightVariants,
  iconSizeVariants,
  paddingScaleVariants,
  borderRadiusVariants,
  focusRingClasses,
  disabledStateClasses,
  gapScaleVariants,
  fontWeightVariants,
  backgroundUtilities,
} from '@/lib/shared-variants';

/**
 * Button component styles
 * Interactive button component with multiple appearance variants
 * Based on Figma Design System Nabat 2.0 - Buttons
 *
 * The launch build rules give each action level one style:
 * - primary: `solid` + `inverse` (#141414, white text)
 * - secondary: `outline` + `neutral` (white, 1px #D9D9D9 border)
 * - tertiary / text link: `transparent` + `brand` (#096151)
 * Map screens use `compact` (40px); auth and settings use `m` (48px).
 */
export const buttonVariants = cva(
  `inline-flex items-center justify-center ${gapScaleVariants.xs} font-button ${fontWeightVariants.medium} transition-all ${focusRingClasses} ${disabledStateClasses}`,
  {
    variants: {
      appearance: {
        solid: '',
        outline: `${backgroundUtilities.transparent} border`,
        transparent: backgroundUtilities.transparent,
      },
      kind: {
        neutral: '',
        brand: '',
        danger: '',
        inverse: '',
      },
      scale: {
        s: `${buttonHeightVariants.s} ${paddingScaleVariants.s} ${textScaleVariants.s} ${borderRadiusVariants.sm}`,
        compact: `${buttonHeightVariants.compact} ${paddingScaleVariants.m} ${textScaleVariants.m} ${borderRadiusVariants.sm}`,
        m: `${buttonHeightVariants.m} ${paddingScaleVariants.m} ${textScaleVariants.m} ${borderRadiusVariants.sm}`,
        l: `${buttonHeightVariants.l} ${paddingScaleVariants.l} ${textScaleVariants.l} ${borderRadiusVariants.sm}`,
      },
      width: {
        auto: 'w-auto',
        full: 'w-full',
      },
    },
    compoundVariants: [
      // Solid appearance variants
      {
        appearance: 'solid',
        kind: 'neutral',
        className:
          'bg-surface-subtle text-foreground hover:bg-surface-subtle/90 active:bg-surface-subtle/80',
      },
      {
        appearance: 'solid',
        kind: 'brand',
        className: 'bg-brand text-white hover:bg-brand/90 active:bg-brand/80',
      },
      {
        appearance: 'solid',
        kind: 'danger',
        className:
          'bg-status-danger text-white hover:bg-status-danger/90 active:bg-status-danger/80',
      },
      {
        appearance: 'solid',
        kind: 'inverse',
        // `bg/contrast` has no tailwind key, and both halves flip under `.dark`.
        className:
          'bg-[var(--bg-contrast)] text-[var(--text-inverse)] hover:opacity-90 active:opacity-80',
      },

      // Outline appearance variants
      {
        appearance: 'outline',
        kind: 'neutral',
        className:
          'border-shades-basic bg-surface text-foreground hover:bg-surface-muted active:bg-surface-subtle',
      },
      {
        appearance: 'outline',
        kind: 'brand',
        className: 'border-brand text-brand hover:bg-brand/10 active:bg-brand/20',
      },
      {
        appearance: 'outline',
        kind: 'danger',
        className:
          'border-status-danger text-status-danger hover:bg-status-danger/10 active:bg-status-danger/20',
      },
      {
        appearance: 'outline',
        kind: 'inverse',
        className: 'border-white text-white hover:bg-white/10 active:bg-white/20',
      },

      // Transparent appearance variants
      {
        appearance: 'transparent',
        kind: 'neutral',
        className: 'text-foreground hover:bg-surface-muted active:bg-surface-subtle',
      },
      {
        appearance: 'transparent',
        kind: 'brand',
        className: 'text-brand hover:bg-brand/10 active:bg-brand/20',
      },
      {
        appearance: 'transparent',
        kind: 'danger',
        className: 'text-status-danger hover:bg-status-danger/10 active:bg-status-danger/20',
      },
      {
        appearance: 'transparent',
        kind: 'inverse',
        className: 'text-white hover:bg-white/10 active:bg-white/20',
      },
    ],
    defaultVariants: {
      appearance: 'solid',
      kind: 'neutral',
      scale: 'm',
      width: 'auto',
    },
  }
);

/**
 * Loading spinner styles
 * Animated spinner for button loading states
 */
export const buttonSpinnerVariants = cva(ANIMATION_CLASSES.spin, {
  variants: {
    scale: {
      s: iconSizeVariants.s,
      compact: iconSizeVariants.m,
      m: iconSizeVariants.m,
      l: iconSizeVariants.l,
    },
  },
  defaultVariants: {
    scale: 'm',
  },
});
